import type { SignalStrength, IndicatorValues } from '../types';

/**
 * Format currency value
 */
export const formatCurrency = (
  value: number | null | undefined,
  currency: string = 'R',
  decimals: number = 2
): string => {
  if (value === null || value === undefined || isNaN(value)) return `${currency} 0.00`;

  const formatted = Math.abs(value).toLocaleString('en-ZA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${currency} ${formatted}`;
};

/**
 * Format large numbers (market cap, volume)
 */
export const formatLargeNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return '0';

  if (value >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;

  return value.toFixed(2);
};

/**
 * Format percentage
 */
export const formatPercent = (
  value: number | null | undefined,
  showSign: boolean = true
): string => {
  if (value === null || value === undefined) return '0.00%';

  const numValue = Number(value);
  if (isNaN(numValue)) return '0.00%';

  const sign = showSign && numValue > 0 ? '+' : '';
  return `${sign}${numValue.toFixed(2)}%`;
};

/**
 * Calculate percentage change
 */
export const calculatePercentChange = (current: number, previous: number): number => {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Determine if market is positive or negative
 */
export const isPositive = (value: number): boolean => {
  return value >= 0;
};

/**
 * Get color class based on value
 */
export const getChangeColor = (value: number): string => {
  if (value > 0) return 'text-emerald-400';
  if (value < 0) return 'text-red-400';
  return 'text-slate-400';
};

/**
 * Get background color class based on value
 */
export const getChangeBgColor = (value: number): string => {
  if (value > 0) return 'bg-emerald-500/20';
  if (value < 0) return 'bg-red-500/20';
  return 'bg-slate-500/20';
};

/**
 * Format date and time
 */
export const formatDate = (date: Date | string, includeTime: boolean = false): string => {
  const d = new Date(date);

  if (includeTime) {
    return d.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return d.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Get relative time (e.g., "2 minutes ago")
 */
export const getRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return formatDate(date);
};

/**
 * Parse symbol to get exchange info
 */
export const parseSymbol = (symbol: string): { code: string; exchange: string; full: string } => {
  const parts = symbol.split('.');
  return {
    code: parts[0],
    exchange: parts[1] || 'US',
    full: symbol,
  };
};

/**
 * Calculate RSI (Relative Strength Index)
 */
export const calculateRSI = (prices: number[], period: number = 14): number | null => {
  if (prices.length < period + 1) return null;

  let gains = 0;
  let losses = 0;

  for (let i = 1; i <= period; i++) {
    const change = prices[i] - prices[i - 1];
    if (change > 0) gains += change;
    else losses -= change;
  }

  const avgGain = gains / period;
  const avgLoss = losses / period;

  if (avgLoss === 0) return 100;

  const rs = avgGain / avgLoss;
  const rsi = 100 - 100 / (1 + rs);

  return rsi;
};

/**
 * Determine signal strength based on multiple indicators
 */
export const getSignalStrength = (indicators: IndicatorValues): SignalStrength => {
  let score = 0;
  let count = 0;

  if (indicators.rsi !== undefined) {
    count++;
    if (indicators.rsi < 30) score += 2; // Strong buy
    else if (indicators.rsi < 50) score += 1; // Buy
    else if (indicators.rsi > 70) score -= 2; // Strong sell
    else if (indicators.rsi > 50) score -= 1; // Sell
  }

  if (indicators.macd !== undefined && indicators.macdSignal !== undefined) {
    count++;
    if (indicators.macd > indicators.macdSignal) score += 1; // Buy
    else score -= 1; // Sell
  }

  if (indicators.sma50 !== undefined && indicators.sma200 !== undefined) {
    count++;
    if (indicators.sma50 > indicators.sma200) score += 1; // Golden cross - buy
    else score -= 1; // Death cross - sell
  }

  if (count === 0) return 'neutral';

  const avgScore = score / count;

  if (avgScore >= 1) return 'strong';
  if (avgScore >= 0.3) return 'moderate';
  if (avgScore <= -1) return 'weak';
  if (avgScore <= -0.3) return 'moderate-sell';

  return 'neutral';
};

/**
 * Debounce function for search and filtering
 */
export const debounce = <T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number = 300
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Sort array of objects by property
 */
export const sortBy = <T extends Record<string, unknown>>(
  array: T[],
  key: keyof T,
  direction: 'asc' | 'desc' = 'asc'
): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal === bVal) return 0;

    const comparison = (aVal as number) > (bVal as number) ? 1 : -1;
    return direction === 'asc' ? comparison : -comparison;
  });
};
